import type { ReactElement } from 'react';
import type { PluginRegistryResolutionState } from '@/presentation/hooks/usePluginRegistryResolution';
import { PluginLoadingScreen } from '@/presentation/components/PluginLoadingScreen';
import { PluginErrorScreen } from '@/presentation/components/PluginErrorScreen';

export interface PluginRegistryResolvingScreenProps {
  state: PluginRegistryResolutionState;
  onRetry: () => void;
}

/**
 * Traduz `PluginRegistryResolutionState` (HUB-237, CA-01/CA-16) para a tela
 * correspondente: carregando/tentando novamente (`PluginLoadingScreen`,
 * reusado da fase de carregamento do plugin — mesma linguagem visual) ou
 * erro final de rede (`PluginErrorScreen`, com o texto "Sem conexão.
 * Verifique a rede." — CA-16). `resolved`/`not-configured`/`session-expired`
 * são desfechos transitórios: o orquestrador (`useActivationFlow`) já troca
 * de `FlowStep` no mesmo encadeamento assíncrono, então nunca chegam a ficar
 * visíveis por mais que um frame — tratados aqui como o estado de
 * carregamento, nunca uma tela em branco.
 */
export function PluginRegistryResolvingScreen({
  state,
  onRetry,
}: PluginRegistryResolvingScreenProps): ReactElement {
  if (state.kind === 'waiting') {
    return (
      <PluginLoadingScreen
        phase={{ kind: 'waiting', attempt: state.attempt, secondsRemaining: state.secondsRemaining }}
      />
    );
  }
  if (state.kind === 'exhausted') {
    return <PluginErrorScreen offline={state.offline} onRetry={onRetry} />;
  }
  const attempt = state.kind === 'attempting' ? state.attempt : 1;
  return <PluginLoadingScreen phase={{ kind: 'loading', attempt }} />;
}
